import { Wrench, Sparkles, Calendar, Package } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { motion } from 'framer-motion';

const SmartToolsSection = () => {
  const isMobile = useIsMobile();
  
  return (
    <section className="section relative py-24 overflow-hidden">
      {/* Background gradient effect */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-violet-900/10 to-transparent" />
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="max-w-6xl mx-auto px-6 relative"
      >
        <div className="flex flex-col space-y-8">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {/* Feature badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-violet-500/10 to-purple-500/10 backdrop-blur-sm border border-violet-500/20">
              <Sparkles className="w-4 h-4 text-violet-400" />
              <span className="text-sm font-medium bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
                Smart Automation
              </span>
            </div>
            
            {/* Icon with glow effect */}
            <motion.div 
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ duration: 0.5, type: "spring" }}
              viewport={{ once: true }}
              className="relative inline-block"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-violet-500 to-purple-500 blur-xl opacity-50" />
              <div className="relative bg-gradient-to-br from-violet-500/20 to-purple-500/20 backdrop-blur-sm border border-violet-500/30 p-4 rounded-2xl">
                <Wrench size={isMobile ? 28 : 32} className="text-white" />
              </div>
            </motion.div>
            
            <h3 className="text-3xl md:text-4xl font-bold">
              <span className="text-white">Munger</span>{' '}
              <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-violet-400 bg-clip-text text-transparent animate-gradient-x">
                Smart Tools
              </span>
            </h3>
            
            <p className="text-xl text-gray-300 leading-relaxed max-w-3xl">
              Set up a{' '}
              <span className="text-white font-medium">Booking Page to schedule your sales team's appointments</span>{' '}
              or generate marketing ideas with Marketing Idea Creator.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="relative group"
            >
              {/* Card glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-violet-500/20 to-purple-500/20 blur-lg opacity-0 group-hover:opacity-60 transition-opacity duration-300" />
              
              {/* Glass morphism card */}
              <div className="relative backdrop-blur-sm bg-gradient-to-br from-gray-800/40 to-gray-900/40 rounded-2xl border border-gray-700/50 shadow-2xl hover:shadow-violet-500/20 transition-all duration-300 overflow-hidden">
                <div className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 h-full">
                  {/* Gradient overlay on image */}
                  <div className="relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent z-10" />
                    <img 
                      src="/lovable-uploads/2aecb9e1-a2fa-4a95-9aae-12a41554432a.png" 
                      alt="Smart Tools - Booking Calendar" 
                      className="w-full h-auto transform group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg bg-gradient-to-br from-violet-500/20 to-purple-500/20 backdrop-blur-sm border border-violet-500/30">
                        <Calendar className="w-5 h-5 text-violet-400" />
                      </div>
                      <h4 className="text-xl font-semibold text-white">Booking Calendar</h4>
                    </div>
                    <p className="text-gray-300 leading-relaxed">
                      Configure your booking page and manage appointments with customers
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="relative group"
            >
              {/* Card glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/20 to-pink-500/20 blur-lg opacity-0 group-hover:opacity-60 transition-opacity duration-300" />
              
              {/* Glass morphism card */}
              <div className="relative backdrop-blur-sm bg-gradient-to-br from-gray-800/40 to-gray-900/40 rounded-2xl border border-gray-700/50 shadow-2xl hover:shadow-purple-500/20 transition-all duration-300 overflow-hidden">
                <div className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 h-full">
                  {/* Gradient overlay on image */}
                  <div className="relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent z-10" />
                    <img 
                      src="/lovable-uploads/9aea9a1b-96f2-4f32-a421-9e721de52b39.png" 
                      alt="Smart Tools - Product Management" 
                      className="w-full h-auto transform group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-sm border border-purple-500/30">
                        <Package className="w-5 h-5 text-purple-400" />
                      </div>
                      <h4 className="text-xl font-semibold text-white">Product Management</h4>
                    </div>
                    <p className="text-gray-300 leading-relaxed">
                      Manage your product profiles and campaign settings in one place
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default SmartToolsSection;
